import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowDownToLine,
  faArrowsRotate,
  faCalendar,
  faCalendarHeart,
  faCamera,
  faChevronLeft,
  faChevronRight,
  faCircleQuestion,
  faHeart,
  faMagnifyingGlass,
  faMapLocationDot,
  faNoteSticky,
  faPartyHorn,
  faPlus,
  faRoute,
  faSliders,
  faSparkles,
  faTowerBroadcast,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useEffect, useState } from 'react';
import {
  modeBlurb,
  modeButtonLabel,
  modeIcon,
  tripModes,
} from '@/design-system/composite/TripModeMenu';
import { Button } from '@/design-system/primitives/Button';
import { IconDisc } from '@/design-system/primitives/IconDisc';
import { Modal } from '@/design-system/primitives/Modal';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';

type TripMode = (typeof tripModes)[number];

// Tutorial-only context lines that complement each mode's short blurb.
// The blurbs themselves come from TripModeMenu so the tagline language
// always matches what users see next to the mode picker.
const modeContext: Record<TripMode, string> = {
  explore: 'Find a city, a town, or a nice rock somewhere.',
  scout: 'Scout for places that catch your eye and save them in your scouted list.',
  curate: 'Sequence your scouted places into the perfect itinerary.',
  travel: 'Reference your itinerary as the trip unfolds.',
};

/** Small inline chip that visually echoes a button/icon shown in the actual
 * UI. Helps readers connect a step's text reference (e.g., "the refresh
 * button") to the icon they should look for in the chrome.
 *
 * Alignment notes (why this looks the way it does):
 *   - The chip wraps an SVG, so we need to optically center a 20px element
 *     against ~14px running text.
 *   - `vertical-align: middle` is the only CSS keyword with a spec-defined
 *     anchor: element midpoint at parent baseline + half x-height. This is
 *     reproducible across browsers, unlike em-based offsets on `inline-flex`
 *     whose baseline depends on the flex spec's "synthesized baseline" rule.
 *   - Spec `middle` lands fractionally below the text's *optical* center
 *     because the anchor is the x-height-half (lowercase-letter midline)
 *     rather than the cap-height midline. A tiny upward `translateY`
 *     compensates without disturbing line-box geometry. */
const IconChip = ({ icon }: { icon: IconDefinition }) => (
  <IconDisc
    icon={icon}
    size="sm"
    tone="neutral"
    className="mx-0.5"
    style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
  />
);

export interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TourStep {
  icon: IconDefinition;
  title: string;
  body: React.ReactNode;
}

const tourSteps: Array<TourStep> = [
  // ───────────────────────────────────────────────────────────────────────
  // Step 1 — How it works: high-level overview of the four-mode workflow.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: faSparkles,
    title: 'How to Trippy Planny',
    body: (
      <>
        <p className="mb-2">
          Trippy Planny has four <strong>modes</strong> — each of which is designed to make planning
          the perfect trip a breeze. Switch between them anytime via the trip menu.
        </p>
        <ul className="space-y-1 mb-2 ml-4 list-disc">
          {tripModes.map((m) => (
            <li key={m}>
              <strong>{modeButtonLabel[m]}</strong>
              <IconChip icon={modeIcon[m]} /> — <em>{modeBlurb[m]}.</em> {modeContext[m]}
            </li>
          ))}
        </ul>
        <p>Let's look at each in more detail.</p>
      </>
    ),
  },

  // ───────────────────────────────────────────────────────────────────────
  // Step 2 — Explore: pick where to go. Only the search bar + radius are
  // visible up front in Explore; the filter bar appears after a search.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: modeIcon.explore,
    title: `${modeButtonLabel.explore} — ${modeBlurb.explore}`,
    body: (
      <>
        <p className="mb-2">
          With no trip selected, you're in <strong>Explore mode</strong> — just you, the map, and
          new places to discover.
        </p>
        <p className="mb-2">
          Use the <strong>search bar</strong>
          <IconChip icon={faMagnifyingGlass} /> at the top to look up a city, a town, or somewhere
          off the beaten path. Trippy Planny pulls up nearby attractions, restaurants, lodging, and
          more around the spot you pick.
        </p>
        <p className="mb-2">
          Adjust the <strong>radius selector</strong> next to the search bar to widen or shrink your
          search area.
        </p>
        <p className="mb-2">
          Once you have some results, the <strong>category filter menu</strong>
          <IconChip icon={faSliders} /> can be used to refine your search — tap any category to
          focus the list and map on just that type of place.
        </p>
        <p>
          When somewhere catches your eye, open the <strong>trip menu</strong>
          <IconChip icon={faMapLocationDot} /> in the header and tap <strong>New trip</strong>
          <IconChip icon={faPlus} /> to start planning your trip.
        </p>
      </>
    ),
  },

  // ───────────────────────────────────────────────────────────────────────
  // Step 3 — Scout: collect places. Scouted list, heart icon, customize +
  // refresh, share/export — all the active "gathering" tools.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: modeIcon.scout,
    title: `${modeButtonLabel.scout} — ${modeBlurb.scout}`,
    body: (
      <>
        <p className="mb-2">
          Select your trip from the <strong>trip menu</strong>
          <IconChip icon={faMapLocationDot} /> and you'll land in <strong>Scout mode</strong>: a map
          and list pf places on the left, and your <strong>scouted list</strong> on the right for
          collecting all the places you're considering for your trip.
        </p>
        <p className="mb-2">
          Tap the <strong>heart icon</strong>
          <IconChip icon={faHeart} /> on any place to drop it into your{' '}
          <strong>scouted list</strong>, or drag it directly from the map.
        </p>
        <p className="mb-2">
          Want more (or fewer) kinds of places to show up? Tap <strong>Customize</strong> on the
          filter bar to choose which kinds of places show up. Click the{' '}
          <strong>refresh button</strong>
          <IconChip icon={faArrowsRotate} /> in the header to refresh the latest places in the area.
        </p>
        <p>
          Each trip in your <strong>trip list</strong> has a <strong>collaborate</strong>
          <IconChip icon={faTowerBroadcast} /> button — invite people by email to view and edit it
          live — plus <strong>export</strong>
          <IconChip icon={faArrowDownToLine} /> to save a standalone copy.
        </p>
      </>
    ),
  },

  // ───────────────────────────────────────────────────────────────────────
  // Step 4 — Curate: plan the day-by-day. Calendar icon, itinerary types,
  // timeline, en-route notes — everything about turning a pile of places
  // into a schedule.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: modeIcon.curate,
    title: `${modeButtonLabel.curate} — ${modeBlurb.curate}`,
    body: (
      <>
        <p className="mb-2">
          Switch to <strong>Curate mode</strong> from the scouted list by clicking the{' '}
          <strong>Curate</strong>
          <IconChip icon={faCalendarHeart} /> button, or by selecting it from the trip menu. You'll
          get a wider two-column view: your scouted places on the left and your daily itinerary on
          the right, just waiting to be filled with fun activities.
        </p>
        <p className="mb-2">
          Click the <strong>calendar icon</strong>
          <IconChip icon={faCalendar} /> on any scouted place to schedule it for a specific day.
        </p>
        <p className="mb-2">
          Pick an <strong>itinerary type</strong>:
        </p>
        <ul className="space-y-1 mb-2 ml-4 list-disc">
          <li>
            <strong>Lodging</strong> — where you're staying; can span multiple days.
          </li>
          <li>
            <strong>Activities</strong> — the fun things you'll do that day.
          </li>
          <li>
            <strong>Logistics</strong> — gas, groceries, parking, and the other bits.
          </li>
        </ul>
        <p className="mb-2">
          Activities are organized on a vertical <strong>timeline</strong> for each day along with
          their scheduled times. Optionally, tap on the <strong>Notes</strong>
          <IconChip icon={faNoteSticky} /> button to add notes to each itinerary item
        </p>
        <p>
          On <strong>Day 1</strong>, an <strong>"On the way"</strong> section lets you scribble
          freeform notes for the drive in — tap <strong>+ Add</strong>
          <IconChip icon={faPlus} /> to jot one down. The last day has a similar{' '}
          <strong>"On the way home"</strong> section for the trip back.
        </p>
      </>
    ),
  },

  // ───────────────────────────────────────────────────────────────────────
  // Step 5 — Travel: on the road. Focused day view + routes.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: modeIcon.travel,
    title: `${modeButtonLabel.travel} — ${modeBlurb.travel}`,
    body: (
      <>
        <p className="mb-2">
          Switch to <strong>Travel mode</strong> while you're travelling. You'll see the active
          day's itinerary alongside a focused map of just those places — no clutter, just where
          you're headed next.
        </p>
        <p className="mb-2">
          The map shows <strong>routes</strong> between your scheduled places each day so you can
          see where you're headed at a glance. Toggle them with the <strong>routes button</strong>
          <IconChip icon={faRoute} /> in the trip menu, and use the day arrows to step through each
          day as your trip unfolds. Scouted and curated places keep their{' '}
          <strong>colored rings</strong> on the map — purple for activities, pink for lodging, teal
          for logistics.
        </p>
        <p>
          Capture some memories on the go by tapping the <strong>camera tile</strong>
          <IconChip icon={faCamera} /> while scouting, curating, or travelling to view and add
          photos.
        </p>
      </>
    ),
  },

  // ───────────────────────────────────────────────────────────────────────
  // Step 6 — Closing.
  // ───────────────────────────────────────────────────────────────────────
  {
    icon: faPartyHorn,
    title: "Let's start exploring!",
    body: (
      <>
        <p>
          Tap the <strong>help button</strong>
          <IconChip icon={faCircleQuestion} /> in the header anytime to see this tour again. Now, go
          plan your next trip! 🎉
        </p>
        {/* Mode-journey visualization — a small reminder of the flow, with
            each mode in its own kawaii palette tint. */}
        <div className="mt-8 flex items-center justify-center gap-2">
          {tripModes.map((m, i) => {
            const tints: Record<(typeof tripModes)[number], string> = {
              explore: 'bg-primary-subtle border-primary-main text-primary-emphasized',
              scout: 'bg-secondary-subtle border-secondary-main text-secondary-emphasized',
              curate: 'bg-tertiary-subtle border-tertiary-main text-tertiary-emphasized',
              travel: 'bg-quaternary-subtle border-quaternary-main text-quaternary-emphasized',
            };
            return (
              <Fragment key={m}>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shadow-sm ${tints[m]}`}
                  >
                    <FontAwesomeIcon icon={modeIcon[m]} style={{ width: 20, height: 20 }} />
                  </div>
                  <SectionEyebrow as="span" size="xs">
                    {modeButtonLabel[m]}
                  </SectionEyebrow>
                </div>
                {i < tripModes.length - 1 && (
                  <FontAwesomeIcon
                    icon={faSparkles}
                    style={{ width: 14, height: 14 }}
                    className="text-secondary-emphasized -mt-4"
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      </>
    ),
  },
];

export const TutorialModal = ({ isOpen, onClose }: TutorialModalProps) => {
  const [stepIndex, setStepIndex] = useState(-1); // -1 = welcome screen

  // Reset to welcome whenever the modal opens
  useEffect(() => {
    if (isOpen) setStepIndex(-1);
  }, [isOpen]);

  const isWelcome = stepIndex === -1;
  const isLastStep = stepIndex === tourSteps.length - 1;
  const currentStep = stepIndex >= 0 ? tourSteps[stepIndex] : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      {isWelcome ? (
        <div className="p-8 text-center">
          {/* Welcome-screen hero: the IconDisc sits center; two sparkles
              orbit around it in opposing directions for a little
              "welcome!" energy. The orbit center matches the disc
              center so the sparkles trace a clean circle around it. */}
          <div className="flex justify-center mb-4">
            <span className="relative inline-flex items-center justify-center">
              <IconDisc icon={faPartyHorn} size="xl" tone="primary" />
              <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 motion-safe:animate-kawaii-orbit motion-reduce:hidden"
                style={{ '--kawaii-orbit-radius': '46px' } as React.CSSProperties}
              >
                <FontAwesomeIcon
                  icon={faSparkles}
                  style={
                    {
                      width: 16,
                      height: 16,
                      '--fa-primary-color': 'var(--color-primary-emphasized)',
                      '--fa-secondary-color': 'var(--color-secondary-emphasized)',
                      '--fa-secondary-opacity': '1',
                    } as React.CSSProperties & { [key: `--fa-${string}`]: string }
                  }
                />
              </span>
              <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 motion-safe:animate-kawaii-orbit-reverse motion-reduce:hidden"
                style={{ '--kawaii-orbit-radius': '52px' } as React.CSSProperties}
              >
                <FontAwesomeIcon
                  icon={faSparkles}
                  style={
                    {
                      width: 12,
                      height: 12,
                      '--fa-primary-color': 'var(--color-secondary-emphasized)',
                      '--fa-secondary-color': 'var(--color-tertiary-emphasized)',
                      '--fa-secondary-opacity': '1',
                    } as React.CSSProperties & { [key: `--fa-${string}`]: string }
                  }
                />
              </span>
            </span>
          </div>
          <h2 className="font-diary text-2xl text-content-text mb-2">Welcome to Trippy Planny!</h2>
          <p className="text-sm text-content-text mb-6 leading-relaxed">
            Trippy Planny helps you discover places, build day-by-day trip itineraries, and share
            them with friends. Want a quick tour?
          </p>
          <div className="flex flex-col gap-2">
            <Button size="sm" className="rounded-full" fullWidth onClick={() => setStepIndex(0)}>
              Let's take a little tour
            </Button>
            <Button variant="ghost" size="xs" fullWidth onClick={onClose}>
              Skip for now
            </Button>
          </div>
        </div>
      ) : currentStep ? (
        <div className="flex flex-col">
          <div className="p-6 pb-4">
            <div className="flex items-center gap-3 mb-3">
              {/* Larger version of the inline IconChip (same neutral tone)
                  so the step header reads as a family member. */}
              <IconDisc icon={currentStep.icon} size="base" tone="neutral" />
              <h3 className="font-diary text-xl text-content-text">{currentStep.title}</h3>
            </div>
            <div className="text-sm text-content-text leading-relaxed h-[380px] overflow-y-auto pr-1">
              {currentStep.body}
            </div>
          </div>

          {/* Step indicator dots */}
          <div className="flex items-center justify-center gap-1.5 px-6 pb-3">
            {tourSteps.map((step, i) => (
              <button
                key={step.title}
                type="button"
                onClick={() => setStepIndex(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex
                    ? 'bg-secondary-emphasized w-6'
                    : 'bg-content-divider w-1.5 hover:bg-content-border'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between px-6 py-4 bg-content-focal/40 border-t border-content-divider">
            <Button
              variant="ghost"
              size="xs"
              icon={faChevronLeft}
              onClick={() => setStepIndex((s) => Math.max(-1, s - 1))}
            >
              Back
            </Button>
            {isLastStep ? (
              <Button size="sm" className="rounded-full" onClick={onClose}>
                Start Exploring
              </Button>
            ) : (
              <Button size="sm" className="rounded-full" onClick={() => setStepIndex((s) => s + 1)}>
                Next
                <FontAwesomeIcon icon={faChevronRight} style={{ width: 10, height: 10 }} />
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
};
